import {differenceInCalendarDays, format, intlFormat} from 'date-fns';

export const millis = 1;
export const seconds = 1000 * millis;
export const minutes = 60 * seconds;
export const hours = 60 * minutes;
export const days = 24 * hours;

/**
 * Returns a new Date each time.
 * @return {Date}
 */
export function currentDate() {
  return new Date();
}

/**
 * Return the date string YYYY-MM-DD, in the local timezone of the date
 *
 * @param {CanBeDate} date
 * @return {string|null}
 */
export function toYearMonthDay(date) {
  if (!date) return null;
  const d = toDate(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return [year, month, day].join('-');
}

/**
 * Return the start of the day.
 * @param {Date} now?
 * @return {Date}
 */
export function startOfDay(now = currentDate()) {
  const res = new Date(now.getTime());
  res.setHours(0, 0, 0, 0);
  return res;
}

/**
 * Return the start of the day in UTC.
 *
 * @param {Date} [now]
 * @return {Date}
 */
export function startOfDayUTC(now = currentDate()) {
  const res = new Date(now.getTime());
  res.setUTCHours(0, 0, 0, 0);
  return res;
}

/**
 * Return the start of the day.
 * @param {Date} now?
 * @return {Date}
 */
export function endOfDay(now = currentDate()) {
  const res = new Date(now.getTime());
  res.setHours(23, 59, 59, 999);
  return res;
}

/**
 * Adds the specified duration (in ms) to the time.
 *
 * @param {Date} now
 * @param {number} duration
 * @return {Date}
 */
export function addTime(now, duration) {
  return new Date(now.getTime() + duration);
}

/**
 * Returns the smallest date from the given dates.
 * @param {...CanBeDate} dates
 * @return {Date}
 */
export function minDate(...dates) {
  let d = undefined;
  for (let i = 0; i < dates.length; i++) {
    if (!dates[i]) continue;
    const di = toDate(dates[i]);
    if (!d || di < d) {
      d = di;
    }
  }
  return d;
}

/**
 * Returns the largest date from the given dates.
 * @param {...CanBeDate} dates
 * @return {Date}
 */
export function maxDate(...dates) {
  let d = undefined;
  for (let i = 0; i < dates.length; i++) {
    if (!dates[i]) continue;
    const di = toDate(dates[i]);
    if (!d || di > d) {
      d = di;
    }
  }
  return d;
}

/**
 * @typedef {String|Date|Number|{toDate:function():Date}} CanBeDate
 */

/**
 * Finds the first possible date out of the given values
 * @param {...(CanBeDate|function():CanBeDate)} dates
 * @return {Date}
 */
export function toDate(...dates) {
  for (let date of dates) {
    if (typeof date === 'function') {
      date = date();
    }
    if (date instanceof Date) {
      return date;
    }
    if (date === null || typeof date === 'undefined') {
      continue;
    }
    if (typeof date.toDate === 'function') return date.toDate();

    try {
      return new Date(date);
    } catch (e) {
      // log.warn('Failed to create date from', date);
    }
  }

  return null;
}

/**
 * Finds the first possible date out of the given values and returns it's toTime value.
 * @param {...(CanBeDate|function():CanBeDate)} dates
 * @return {number | false}
 */
export function toTime(...dates) {
  const d = toDate(...dates);
  return d ? d.getTime() : false;
}

/**
 * Checks if two dates are on the same day.
 *
 * @param {Date} first
 * @param {Date} second
 * @return {boolean}
 */
export function isSameDay(first, second) {
  return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
}

/**
 * Checks if two dates are on the same UTC day.
 *
 * @param {CanBeDate} d1
 * @param {CanBeDate} d2
 * @return {boolean}
 */
export function isSameDayUtc(d1, d2) {
  if (!d1 || !d2) {
    return false;
  }
  d1 = toDate(d1);
  d2 = toDate(d2);
  return d1 && d2 &&
      d1.getUTCFullYear() === d2.getUTCFullYear() &&
      d1.getUTCMonth() === d2.getUTCMonth() &&
      d1.getUTCDate() === d2.getUTCDate();
}

const localeCode = window.navigator && window.navigator.language || 'en-gb';
const localOptions = {locale: localeCode};
const ordinalRules = new Intl.PluralRules(localeCode, {type: 'ordinal'});
const ordinals = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th'
};

/**
 * Takes a string for formatting a date, formatting values can be found at https://date-fns.org/v2.25.0/docs/format
 * If "p" is passed in as the formatString, the function will use date-fns intlFormat
 * function (https://date-fns.org/v2.25.0/docs/intlFormat) to return the time formatted to locale HH:mm.
 *
 * @param {Date} time
 * @param {string} formatString
 * @return {string}
 */
export function formatLocal(time, formatString) {
  if (formatString === 'p') {
    return intlFormat(time, {
      hour: 'numeric',
      minute: 'numeric'
    }, localOptions);
  } else {
    return format(time, formatString);
  }
}

/**
 * Format a date relative to 'now'. If they are the same day, nothing is returned. If they are one day apart, then
 * yesterday or tomorrow is returned. If within 6 days, then the day-of-the-week is returned. Otherwise, a
 * date and month is returned, with optional year if the years are different.
 *
 * @param {Date} date
 * @param {Date} now
 * @return {string}
 */
export function formatDateRelative(date, now) {
  if (date.getDate() === now.getDate()) {
    return '';
  }
  if (date.getDate() === now.getDate() - 1) {
    return 'Yesterday';
  }
  if (date.getDate() === now.getDate() + 1) {
    return 'Tomorrow';
  }
  const days = Math.abs(differenceInCalendarDays(date, now));
  if (days > 6) {
    const day = intlFormat(date, {day: 'numeric'}, localOptions);
    const withOrdinal = day + ordinalSuffix(day);
    const month = intlFormat(date, {month: 'long'}, localOptions);
    let year = '';
    if (date.getFullYear() !== now.getFullYear()) {
      year = ' ' + intlFormat(date, {year: 'numeric'}, localOptions);
    }
    return `${withOrdinal} ${month}${year}`;
  }
  return intlFormat(date, {weekday: 'long'}, localOptions);
}

/**
 * @param {number|string} num
 * @return {string}
 */
export function ordinalSuffix(num) {
  const i = parseInt(num);
  return ordinals[ordinalRules.select(i)];
}
